import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "../../components/ui/Toast";
import {
  Upload,
  UserPlus,
  Shuffle,
  Save,
  Mail,
  X,
  Copy,
  Users,
  GripVertical,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;
const TEAM_CAPACITY = 6;

// ─── CSV parser ───────────────────────────────────────────────────────────────
function parseCsv(text) {
  if (!text) return [];
  const rawLines = text.split(/\r?\n/);
  const lines = rawLines.map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];

  const removeOuterQuotes = (value = "") => {
    let v = value.trim();
    if (v.startsWith('"') && v.endsWith('"') && v.length >= 2) v = v.slice(1, -1);
    return v;
  };
  const stripOuterQuotes = (value = "") => value.replace(/^"+|"+$/g, "").trim();
  const normalizeGender = (raw) => {
    const g = stripOuterQuotes(raw || "").toLowerCase();
    if (["f", "femme", "female"].includes(g)) return "F";
    if (["h", "homme", "male"].includes(g)) return "H";
    if (["nb", "non-binaire", "non binaire"].includes(g)) return "NB";
    return "U";
  };

  const firstClean = removeOuterQuotes(lines[0]);
  const sep = firstClean.includes(";") ? ";" : ",";
  const headerClean = removeOuterQuotes(lines[0]);
  const headerCells = headerClean.split(sep).map((c) => stripOuterQuotes(c).toLowerCase());
  const hasHeader =
    headerCells.length >= 1 &&
    (["name", "nom", "prenom", "prénom"].includes(headerCells[0]) ||
      headerCells.includes("genre") ||
      headerCells.includes("gender"));
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows = [];

  dataLines.forEach((raw, idx) => {
    if (!raw.trim()) return;
    const cleanedLine = removeOuterQuotes(raw);
    if (!cleanedLine) return;
    let cells = cleanedLine.split(sep).map(stripOuterQuotes);
    if (cells.every((c) => !c)) return;

    let genderRaw = "", team = "", name = "";
    if (cells.length === 1) { name = cells[0]; }
    else if (cells.length === 2) { name = cells[0]; team = cells[1]; }
    else {
      genderRaw = cells[cells.length - 1];
      team = cells[cells.length - 2];
      name = cells.slice(0, cells.length - 2).join(sep);
    }

    name = stripOuterQuotes(name);
    team = stripOuterQuotes(team);
    if (!name) name = `Employé ${idx + 1}`;

    rows.push({ id: `csv-${Date.now()}-${idx}`, name, team, gender: normalizeGender(genderRaw) });
  });
  return rows;
}

// ─── Helpers visuels ──────────────────────────────────────────────────────────
function genderColorClass(g) {
  switch (g) {
    case "F": return "bg-pink-500 text-white";
    case "H": return "bg-blue-500 text-white";
    case "NB": return "bg-purple-500 text-white";
    default: return "bg-slate-400 text-white";
  }
}
function getCardBg(g) {
  switch (g) {
    case "F": return "bg-pink-50 border-pink-100";
    case "H": return "bg-blue-50 border-blue-100";
    case "NB": return "bg-purple-50 border-purple-100";
    default: return "bg-slate-50 border-slate-100";
  }
}
function getInitials(name) {
  return name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
}
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildEmployeesAndTeamsFromLayout(serverTeams, selectedPack) {
  if (!selectedPack) return { employees: [], teams: [] };
  const memberMap = new Map();
  let counter = 0;
  const now = Date.now();

  const getOrCreateMember = (raw) => {
    if (!raw) return null;
    const key = `${raw.name || ""}|||${raw.team || ""}|||${raw.gender || "U"}`;
    if (memberMap.has(key)) return memberMap.get(key);
    const emp = { ...raw, id: raw.id || `saved-${now}-${counter++}`, gender: raw.gender || "U" };
    memberMap.set(key, emp);
    return emp;
  };

  const teams = selectedPack.sessions.map((session, idx) => {
    const serverTeam =
      serverTeams.find((t) => t.sessionId === session.id || t.sessionCode === session.code) ||
      serverTeams[idx];
    const members = (serverTeam?.members || []).map(getOrCreateMember).filter(Boolean);
    return { id: idx, session, members };
  });

  return { employees: Array.from(memberMap.values()), teams };
}

// ─── MemberCard (drag & drop) ─────────────────────────────────────────────────
function MemberCard({ member, onDragStart, onDragEnd, onRemove, removeLabel = "Retirer" }) {
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`flex items-center justify-between rounded-xl px-3 py-2 border cursor-move shadow-sm transition-all ${getCardBg(member.gender)}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center text-[11px] font-semibold shadow-sm shrink-0 ${genderColorClass(member.gender)}`}>
          {getInitials(member.name)}
        </span>
        <div>
          <p className="font-semibold text-slate-900 text-xs">{member.name}</p>
          <p className="text-[10px] text-slate-500">{member.team || "—"}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onRemove}
          className="text-[10px] px-2 py-1 rounded-full bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 transition"
        >
          {removeLabel}
        </button>
        <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
      </div>
    </li>
  );
}

// ─── TeamBuilder principal ────────────────────────────────────────────────────
function TeamBuilder({ sessions, guideUrl = "/guide" }) {
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [criteria, setCriteria] = useState({ random: false, genderBalance: false, mixTeams: false });
  const [teams, setTeams] = useState([]);
  const [newEmployee, setNewEmployee] = useState({ name: "", team: "", gender: "U" });
  const [dragged, setDragged] = useState(null);
  const [hoverTeamId, setHoverTeamId] = useState(null);
  const [hoverPool, setHoverPool] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);

  const toast = useToast();

  // ── Bundles éligibles (extended uniquement) ──────────────────────────────────
  const bundles = useMemo(() => {
    const filtered = sessions.filter((s) => s.status !== "expired" && s.status !== "used");
    const isExtended = (s) => String(s?.packType || "").toLowerCase() === "extended";
    const grouped = filtered.reduce((acc, s) => {
      const key = s.paymentId || `solo-${s.id}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(s);
      return acc;
    }, {});
    const arr = Object.entries(grouped).map(([key, list]) => ({
      id: key,
      sessions: list,
      sortKey: Math.max(...list.map((s) => Number(s.id) || 0)),
      eligible: list.every(isExtended),
    }));
    arr.sort((a, b) => b.sortKey - a.sortKey);
    return arr.filter((b) => b.eligible).map(({ id, sessions }) => ({ id, sessions }));
  }, [sessions]);

  const selectedPack = bundles.find((b) => b.id === selectedBundle);
  const nbTeams = selectedPack ? selectedPack.sessions.length : 0;
  const hasAnyActiveSessions = useMemo(
    () => sessions.some((s) => s.status !== "expired" && s.status !== "used"),
    [sessions]
  );
  const hasEligible = bundles.length > 0;

  // ── Chargement layout sauvegardé ─────────────────────────────────────────────
  useEffect(() => {
    setTeams([]);
    if (!selectedBundle || !selectedPack) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get(`${API}/api/team-layouts/${selectedBundle}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.data || !Array.isArray(res.data.teams) || res.data.teams.length === 0) return;
        const { employees: e, teams: t } = buildEmployeesAndTeamsFromLayout(res.data.teams, selectedPack);
        setEmployees(e);
        setTeams(t);
        toast.success("Répartition chargée depuis la sauvegarde.");
      })
      .catch((err) => { if (err?.response?.status !== 404) console.error(err); });
  }, [selectedBundle, selectedPack]);

  // ── CSV ──────────────────────────────────────────────────────────────────────
  const handleFileCsv = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCsv(ev.target.result);
      if (!parsed.length) { toast.error("Aucun employé détecté dans ce fichier CSV."); return; }
      setEmployees((prev) => [...prev, ...parsed]);
      toast.success(`${parsed.length} employé${parsed.length > 1 ? "s" : ""} importé${parsed.length > 1 ? "s" : ""}.`);
    };
    reader.readAsText(file, "utf-8");
  };

  // ── Génération ───────────────────────────────────────────────────────────────
  const handleGenerate = () => {
    if (!selectedPack) { toast.error("Sélectionnez d'abord un pack."); return; }
    if (employees.length === 0) { toast.error("Ajoutez au moins un employé."); return; }

    let ordered = [...employees];
    if (criteria.random) {
      ordered = shuffleArray(ordered);
    } else {
      if (criteria.genderBalance) {
        const f = ordered.filter((e) => e.gender === "F");
        const m = ordered.filter((e) => e.gender === "H");
        const nb = ordered.filter((e) => e.gender === "NB");
        const u = ordered.filter((e) => e.gender === "U");
        ordered = [...f, ...m, ...nb, ...u];
      }
      if (criteria.mixTeams) {
        ordered = [...ordered].sort((a, b) => (a.team || "").localeCompare(b.team || ""));
      }
    }

    const newTeams = Array.from({ length: nbTeams }, (_, i) => ({
      id: i, session: selectedPack.sessions[i], members: [],
    }));
    ordered.forEach((emp, idx) => newTeams[idx % nbTeams].members.push(emp));
    setTeams(newTeams);
  };

  // ── Sauvegarde ───────────────────────────────────────────────────────────────
  const handleSaveLayout = async () => {
    if (!selectedBundle) { toast.error("Sélectionnez un pack avant de sauvegarder."); return; }
    if (teams.length === 0) { toast.error("Générez d'abord une répartition."); return; }
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/team-layouts`,
        { bundleId: selectedBundle, teams: teams.map((t) => ({ sessionId: t.session.id, sessionCode: t.session.code, members: t.members })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Répartition enregistrée avec succès.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // ── Drag & drop ──────────────────────────────────────────────────────────────
  const handleMove = (empId, fromTeamId, toTeamId) => {
    setTeams((prev) => {
      const copy = prev.map((t) => ({ ...t, members: [...t.members] }));
      const employee = employees.find((e) => e.id === empId);
      if (!employee) return prev;
      if (fromTeamId != null) {
        const from = copy.find((t) => t.id === fromTeamId);
        if (from) { const i = from.members.findIndex((m) => m.id === empId); if (i !== -1) from.members.splice(i, 1); }
      }
      if (toTeamId != null) {
        const to = copy.find((t) => t.id === toTeamId);
        if (!to) return prev;
        if (!to.members.some((m) => m.id === empId)) to.members.push(employee);
      }
      return copy;
    });
  };

  const handleDragStart = (memberId, fromTeamId) => setDragged({ memberId, fromTeamId });
  const handleDragEnd = () => { setDragged(null); setHoverTeamId(null); setHoverPool(false); };
  const handleDragOverTeam = (e, teamId) => { e.preventDefault(); setHoverTeamId(teamId); setHoverPool(false); };
  const handleDropOnTeam = (teamId) => { if (dragged) handleMove(dragged.memberId, dragged.fromTeamId, teamId); setDragged(null); setHoverTeamId(null); };
  const handleDragOverPool = (e) => { e.preventDefault(); setHoverPool(true); setHoverTeamId(null); };
  const handleDropOnPool = () => { if (dragged) handleMove(dragged.memberId, dragged.fromTeamId, null); setDragged(null); setHoverPool(false); };

  // ── Mail template ────────────────────────────────────────────────────────────
  const buildEmailTemplate = (team) => {
    const frontBaseUrl = process.env.REACT_APP_FRONT_BASE_URL || "http://localhost:3000";
    const dateStr = team.session.startAt ? new Date(team.session.startAt).toLocaleDateString() : "à définir";
    const membersLines = team.members.length === 0
      ? "- (aucun membre)"
      : team.members.map((m) => `- ${m.name}${m.team ? ` (${m.team})` : ""}`).join("\n");

    return {
      subject: `Escape Game digital — Session équipe ${team.id + 1} — Code ${team.session.code}`,
      body: `Bonjour,

Vous faites partie de l'équipe ${team.id + 1} pour notre prochaine session d'escape game digital.

Code session : ${team.session.code}
Date : ${dateStr}

Composition de votre équipe :
${membersLines}

Pour rejoindre la session le jour J :
1. Lancez une visio avec votre équipe (Teams / Meet / Zoom…)
2. Ouvrez la page de jeu : ${frontBaseUrl}/play
3. Entrez le code session : ${team.session.code}
4. Créez votre profil joueur et suivez les instructions

Merci de vous connecter quelques minutes en avance.

Bonne partie,
L'équipe RH`,
    };
  };

  // ── Capacité globale ──────────────────────────────────────────────────────────
  const assignedIds = new Set(teams.flatMap((t) => t.members.map((m) => m.id)));
  const unassigned = employees.filter((e) => !assignedIds.has(e.id));
  const totalCapacity = nbTeams * TEAM_CAPACITY;
  const totalAssigned = teams.reduce((sum, t) => sum + t.members.length, 0);

  const capacityState = (() => {
    if (!selectedPack || teams.length === 0) return null;
    if (totalAssigned > totalCapacity) return { type: "error", msg: `${totalAssigned - totalCapacity} personne${totalAssigned - totalCapacity > 1 ? "s" : ""} de trop (${totalAssigned}/${totalCapacity}). Retirez des joueurs ou prévoyez plus de sessions.` };
    if (totalAssigned < totalCapacity && totalAssigned > 0) return { type: "warn", msg: `${totalCapacity - totalAssigned} place${totalCapacity - totalAssigned > 1 ? "s" : ""} libre${totalCapacity - totalAssigned > 1 ? "s" : ""} (${totalAssigned}/${totalCapacity}).` };
    if (totalAssigned === totalCapacity) return { type: "ok", msg: `Toutes les places sont occupées (${totalAssigned}/${totalCapacity}).` };
    return null;
  })();

  // ─────────────────────────────────────────────────────────────────────────────
  // Cas : aucune session éligible
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasEligible) {
    return (
      <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-900 text-orange-300 mb-3">
          <Users className="h-5 w-5" />
        </div>
        <p className="text-sm font-semibold text-slate-800 mb-1">
          {hasAnyActiveSessions ? "Aucune session éligible" : "Aucune session active"}
        </p>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          {hasAnyActiveSessions
            ? "Le créateur d'équipe est disponible uniquement pour les sessions en formule Semaine flexible. Vos sessions actuelles ne permettent pas d'utiliser cet outil."
            : "Achetez des sessions en formule Semaine flexible pour utiliser cet outil."}
        </p>
        <a href={guideUrl} className="mt-3 inline-flex text-xs font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
          En savoir plus dans le guide
        </a>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Cas normal
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="mt-2 space-y-6">

      {/* ── Étape 1 : Pack + critères ── */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.16em] mb-3">
          1 · Choisissez un pack
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Sélection du pack */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <label className="block text-xs font-medium text-slate-700 mb-2">Pack de sessions</label>
            <select
              value={selectedBundle || ""}
              onChange={(e) => setSelectedBundle(e.target.value || null)}
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <option value="">— Sélectionner —</option>
              {bundles.map((b) => {
                const first = b.sessions[0];
                const count = b.sessions.length;
                return (
                  <option key={b.id} value={b.id}>
                    {b.id.startsWith("solo")
                      ? `Session : ${first.code}`
                      : `Pack #${b.id} — ${count} session${count > 1 ? "s" : ""}`}
                  </option>
                );
              })}
            </select>
            {selectedPack && (
              <p className="mt-2 text-[11px] text-slate-500">
                <span className="font-medium text-slate-700">{selectedPack.sessions.length} équipe{selectedPack.sessions.length > 1 ? "s" : ""}</span>
                {" "}· Codes : <span className="font-mono text-slate-700">{selectedPack.sessions.map((s) => s.code).join(", ")}</span>
              </p>
            )}
          </div>

          {/* Critères */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <label className="block text-xs font-medium text-slate-700 mb-2">Critères de répartition</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "random", label: "Aléatoire" },
                { key: "genderBalance", label: "Équilibre genre", disabled: criteria.random },
                { key: "mixTeams", label: "Mélanger les services", disabled: criteria.random },
              ].map(({ key, label, disabled }) => (
                <label
                  key={key}
                  className={`inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs shadow-sm cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-indigo-300"}`}
                >
                  <input
                    type="checkbox"
                    checked={criteria[key]}
                    disabled={disabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (key === "random") {
                        setCriteria({ random: checked, genderBalance: false, mixTeams: false });
                      } else {
                        setCriteria((c) => ({ ...c, [key]: checked }));
                      }
                    }}
                  />
                  <span className="font-medium">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Étape 2 : Employés ── */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.16em] mb-3">
          2 · Ajoutez vos participants
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Import + ajout manuel */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            {/* Import CSV */}
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-semibold text-white cursor-pointer shadow-sm transition">
                <Upload className="h-3.5 w-3.5" />
                Importer un CSV
                <input
                  type="file"
                  accept=".csv"
                  className="absolute opacity-0 w-0 h-0 pointer-events-none"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileCsv(f); e.target.value = ""; }}
                />
              </label>
              <span className="text-[11px] text-slate-400">ou ajoutez à la main</span>
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-500 leading-relaxed">
              Format : <span className="font-mono">nom,service,genre</span> — ex : <span className="font-mono">Marie Dupont,Marketing,F</span>.
              Séparateur <span className="font-mono">,</span> ou <span className="font-mono">;</span>. L'en-tête et le genre sont facultatifs.
            </div>

            {/* Ajout manuel */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
              <p className="text-[11px] font-medium text-slate-600">Ajouter manuellement</p>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Nom & prénom"
                  className="flex-1 min-w-[120px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
                <input
                  type="text"
                  value={newEmployee.team}
                  onChange={(e) => setNewEmployee((p) => ({ ...p, team: e.target.value }))}
                  placeholder="Service (optionnel)"
                  className="flex-1 min-w-[120px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
                <select
                  value={newEmployee.gender}
                  onChange={(e) => setNewEmployee((p) => ({ ...p, gender: e.target.value }))}
                  className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <option value="U">Genre</option>
                  <option value="F">F</option>
                  <option value="H">H</option>
                  <option value="NB">NB</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    if (!newEmployee.name.trim()) { toast.error("Merci d'indiquer un nom."); return; }
                    setEmployees((p) => [...p, { id: `manual-${Date.now()}`, name: newEmployee.name.trim(), team: newEmployee.team.trim(), gender: newEmployee.gender || "U" }]);
                    setNewEmployee({ name: "", team: "", gender: "U" });
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs font-semibold text-white shadow-sm transition"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Ajouter
                </button>
              </div>
            </div>
          </div>

          {/* Liste des employés */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-700">
                Participants <span className="text-slate-400">({employees.length})</span>
              </p>
              {employees.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setEmployees([]); setTeams([]); }}
                  className="text-[10px] text-rose-500 hover:text-rose-600 transition"
                >
                  Tout effacer
                </button>
              )}
            </div>
            {employees.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun participant pour l'instant.</p>
            ) : (
              <div className="max-h-52 overflow-auto space-y-1.5 pr-1">
                {employees.map((e) => (
                  <div key={e.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg border border-slate-100 bg-slate-50 text-xs">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-semibold shrink-0 ${genderColorClass(e.gender)}`}>
                        {getInitials(e.name)}
                      </span>
                      <div>
                        <p className="font-semibold text-slate-800">{e.name}</p>
                        <p className="text-[10px] text-slate-400">{e.team || "—"}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEmployees((p) => p.filter((x) => x.id !== e.id))}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 transition"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Étape 3 : Actions ── */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.16em] mb-3">
          3 · Générez et ajustez
        </p>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-sm font-semibold text-white shadow-sm transition"
            >
              <Shuffle className="h-4 w-4" />
              Générer les équipes
            </button>
            <button
              type="button"
              onClick={handleSaveLayout}
              disabled={saving || teams.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-xl text-sm font-semibold text-white shadow-sm transition"
            >
              <Save className="h-4 w-4" />
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button
              type="button"
              onClick={() => setShowMailModal(true)}
              disabled={teams.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 rounded-xl text-sm font-semibold text-white shadow-sm transition"
            >
              <Mail className="h-4 w-4" />
              Générer les mails
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            Une fois les équipes générées, glissez les membres d'une équipe à l'autre pour ajuster manuellement.
          </p>

          {/* Bande capacité */}
          {capacityState && (
            <div className={`mt-3 rounded-lg px-3 py-2 text-xs border ${
              capacityState.type === "error" ? "bg-rose-50 text-rose-800 border-rose-200" :
              capacityState.type === "warn"  ? "bg-amber-50 text-amber-800 border-amber-200" :
              "bg-emerald-50 text-emerald-800 border-emerald-200"
            }`}>
              {capacityState.msg}
            </div>
          )}
        </div>
      </div>

      {/* ── Drag & drop : pool + équipes ── */}
      {teams.length > 0 && (
        <div className="grid lg:grid-cols-[240px_1fr] gap-4">

          {/* Pool non affectés */}
          <div
            onDragOver={handleDragOverPool}
            onDrop={handleDropOnPool}
            className={`rounded-xl border p-4 bg-slate-50 min-h-[120px] transition-all ${
              hoverPool ? "border-emerald-400 shadow-[0_0_0_1px_rgba(16,185,129,0.3)]" : "border-slate-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-700">Non affectés <span className="text-slate-400">({unassigned.length})</span></p>
              <span className="text-[10px] text-slate-400">Déposez ici pour retirer</span>
            </div>
            {unassigned.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic">Tous les participants sont placés.</p>
            ) : (
              <ul className="space-y-2">
                {unassigned.map((p) => (
                  <MemberCard
                    key={p.id}
                    member={p}
                    onDragStart={() => handleDragStart(p.id, null)}
                    onDragEnd={handleDragEnd}
                    onRemove={() => setEmployees((prev) => prev.filter((e) => e.id !== p.id))}
                    removeLabel="Suppr."
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Équipes */}
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {teams.map((t) => {
              const count = t.members.length;
              const isUnder = count > 0 && count < 4;
              const isOver = count > TEAM_CAPACITY;
              const isOk = count >= 4 && count <= TEAM_CAPACITY;

              const borderClass = isUnder || isOver ? "border-rose-300" : isOk ? "border-emerald-300" : "border-slate-200";
              const badgeClass = isUnder || isOver
                ? "bg-rose-100 text-rose-700"
                : isOk
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-600";

              return (
                <div
                  key={t.id}
                  onDragOver={(e) => handleDragOverTeam(e, t.id)}
                  onDrop={() => handleDropOnTeam(t.id)}
                  className={`rounded-xl border p-4 bg-slate-50 transition-all ${
                    hoverTeamId === t.id
                      ? "border-emerald-400 shadow-[0_0_0_1px_rgba(16,185,129,0.3)]"
                      : borderClass
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-[0.14em]">Équipe {t.id + 1}</p>
                      <p className="text-xs font-semibold text-slate-900">
                        Code : <span className="font-mono text-indigo-600">{t.session.code}</span>
                      </p>
                      {t.session.startAt && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {new Date(t.session.startAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeClass}`}>
                      {count}/{TEAM_CAPACITY}
                    </span>
                  </div>

                  {(isUnder || isOver) && (
                    <p className="text-[10px] text-rose-600 mb-2">
                      {isUnder ? "Minimum 4 joueurs requis." : `Maximum ${TEAM_CAPACITY} joueurs.`}
                    </p>
                  )}

                  {t.members.length === 0 ? (
                    <p className="text-[11px] text-slate-400 italic">Glissez des membres ici.</p>
                  ) : (
                    <ul className="space-y-2">
                      {t.members.map((m) => (
                        <MemberCard
                          key={m.id}
                          member={m}
                          onDragStart={() => handleDragStart(m.id, t.id)}
                          onDragEnd={handleDragEnd}
                          onRemove={() => handleMove(m.id, t.id, null)}
                          removeLabel="Retirer"
                        />
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Modale mails ── */}
      {showMailModal && teams.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-xl">

            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-slate-900">Mails par équipe</h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Un mail par équipe, avec le code session et la composition. Copiez-collez dans votre messagerie.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowMailModal(false)}
                className="shrink-0 text-slate-400 hover:text-slate-600 transition"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {teams.map((t) => {
                const { subject, body } = buildEmailTemplate(t);
                const full = `Objet : ${subject}\n\n${body}`;

                return (
                  <div key={t.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-900">
                        Équipe {t.id + 1} · Code <span className="font-mono text-indigo-600">{t.session.code}</span>
                        {t.session.startAt && (
                          <span className="ml-2 font-normal text-slate-400">
                            · {new Date(t.session.startAt).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(subject);
                              toast.success(`Objet équipe ${t.id + 1} copié.`);
                            } catch { toast.error("Impossible de copier."); }
                          }}
                          className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition shadow-sm"
                        >
                          <Copy className="h-3 w-3" />
                          Objet
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(body);
                              toast.success(`Corps équipe ${t.id + 1} copié.`);
                            } catch { toast.error("Impossible de copier."); }
                          }}
                          className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white shadow-sm transition"
                        >
                          <Copy className="h-3 w-3" />
                          Corps du mail
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <label className="text-[10px] text-slate-400 font-medium block mb-1">Objet</label>
                        <input readOnly value={subject}
                          className="w-full text-[11px] bg-white border border-slate-200 rounded-lg px-3 py-2 font-mono text-slate-700 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 font-medium block mb-1">Corps</label>
                        <textarea readOnly value={body}
                          className="w-full h-40 text-[11px] leading-relaxed bg-white border border-slate-200 rounded-lg p-3 font-mono resize-none text-slate-700 focus:outline-none" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamBuilder;